// Product media gallery navigation and mobile variant filtering v1.3.0.
// v1.3.0 (Task 106): a repeated setActiveMedia for the media that is already first no longer re-inserts
// it, and the live-region announcement no longer reassigns src of a loaded image. Both repainted the
// main photo as a new LCP candidate when Globo re-synced the same variant seconds after load.
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        current: this.querySelector('[data-eb-gallery-current]'),
        total: this.querySelector('[data-eb-gallery-total]'),
        navigation: this.querySelector('[data-eb-main-gallery-navigation]'),
        previous: this.querySelector('[data-eb-gallery-previous]'),
        next: this.querySelector('[data-eb-gallery-next]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.mobileMql = window.matchMedia('(max-width: 749px)');
      this.variantMediaBoundaries = this.getVariantMediaBoundaries();
      this.handleViewportChange = () => this.setActiveVariant(this.dataset.ebSelectedVariantId);
      this.handlePreviousClick = () => this.navigateMedia(-1);
      this.handleNextClick = () => this.navigateMedia(1);

      this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));

      if (!this.elements.thumbnails) return;

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      if (this.mobileViewportListening) return;

      this.mobileViewportListening = true;
      this.mobileMql.addEventListener('change', this.handleViewportChange);
      this.elements.previous?.addEventListener('click', this.handlePreviousClick);
      this.elements.next?.addEventListener('click', this.handleNextClick);
      this.setActiveVariant(this.dataset.ebSelectedVariantId);
    }

    disconnectedCallback() {
      this.mobileMql.removeEventListener('change', this.handleViewportChange);
      this.elements.previous?.removeEventListener('click', this.handlePreviousClick);
      this.elements.next?.removeEventListener('click', this.handleNextClick);
      this.mobileViewportListening = false;
    }

    onSlideChanged(event) {
      if (!event || !event.detail || !event.detail.currentElement) return;

      this.userMediaId = event.detail.currentElement.dataset.mediaId;
      this.setActiveMediaState(event.detail.currentElement);
      this.updateGalleryMeta(event.detail.currentElement);
      this.updateNavigation(event.detail.currentElement);
      if (!this.elements.thumbnails) return;

      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
      );
      if (thumbnail) {
        this.setActiveThumbnail(thumbnail);
      }    
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-eb-media-position][data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      if (prepend) {
        // Globo swatches re-sync the variant image late (10-15 s after load) with the same variant.
        if (this.keepUserMedia()) return;
        this.rememberGalleryState();
      } else {
        this.userMediaId = mediaId;
      }

      this.setActiveMediaState(activeMedia);

      if (prepend) {
        if (activeMedia.parentElement.firstElementChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      runAfterLayout(() => {
        if (this.mobileMql.matches) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
        } else if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.mobileMql.matches && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);
      this.updateGalleryMeta(activeMedia);
      this.updateNavigation(activeMedia);

      if (!this.elements.thumbnails) {
        this.announceLiveRegion(activeMedia, this.getNavigableMedia().indexOf(activeMedia) + 1);
        return;
      }
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!activeThumbnail) return;
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveVariant(variantId) {
      if (!this.elements.viewer) return;

      this.dataset.ebSelectedVariantId = variantId || '';
      const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-eb-media-position]'));
      const boundaries = this.variantMediaBoundaries;
      const selectedBoundaryIndex = boundaries.findIndex((boundary) => String(boundary.id) === String(variantId));
      const selectedBoundary = boundaries[selectedBoundaryIndex];
      const nextBoundary = boundaries[selectedBoundaryIndex + 1];
      const hasReliableMapping = boundaries.length > 1
        && boundaries[0].position === 1
        && selectedBoundaryIndex >= 0
        && selectedBoundary.position > 0;
      const belongsToSelectedVariant = (mediaItem) => {
        const position = Number(mediaItem.dataset.ebMediaPosition);
        return position >= selectedBoundary.position
          && (!nextBoundary || position < nextBoundary.position);
      };
      const usesGloboVariantImages = Boolean(
        this.closest('product-info')
          ?.querySelector('.globo-swatch-product-detail [data-use-variant-image="true"]')
      );
      const canFilter = this.mobileMql.matches && hasReliableMapping;

      if (hasReliableMapping && usesGloboVariantImages) {
        mediaItems.forEach((mediaItem) => {
          const belongsToVariant = belongsToSelectedVariant(mediaItem);
          mediaItem.classList.toggle('hidden', !belongsToVariant);
          mediaItem.dataset.ebActiveVariantMedia = String(belongsToVariant);
        });
        this.syncGloboThumbnails(mediaItems);
      } else {
        mediaItems.forEach((mediaItem) => { delete mediaItem.dataset.ebActiveVariantMedia; });
        this.querySelectorAll('[data-eb-active-variant-thumbnail]')
          .forEach((thumbnail) => { delete thumbnail.dataset.ebActiveVariantThumbnail; });
      }

      mediaItems.forEach((mediaItem) => {
        if (!canFilter) {
          if (mediaItem.dataset.ebMobileHidden === 'true') mediaItem.hidden = false;
          delete mediaItem.dataset.ebMobileHidden;
          return;
        }

        const belongsToVariant = belongsToSelectedVariant(mediaItem);
        if (belongsToVariant) {
          if (mediaItem.dataset.ebMobileHidden === 'true') mediaItem.hidden = false;
          delete mediaItem.dataset.ebMobileHidden;
        } else {
          mediaItem.hidden = true;
          mediaItem.dataset.ebMobileHidden = 'true';
        }
      });

      let visibleMedia = mediaItems.filter((mediaItem) => !mediaItem.hidden);
      if (canFilter && visibleMedia.length === 0) {
        mediaItems.forEach((mediaItem) => {
          if (mediaItem.dataset.ebMobileHidden === 'true') mediaItem.hidden = false;
          delete mediaItem.dataset.ebMobileHidden;
        });
        visibleMedia = mediaItems.filter((mediaItem) => !mediaItem.hidden);
      }

      // Rewind only when the variant or its media set really changed, not on a repeated call.
      if (this.getGalleryStateKey() !== this.galleryStateKey) {
        this.rememberGalleryState();
        this.elements.viewer.resetPages?.();
        this.resetFilteredSlider(visibleMedia.length || mediaItems.length);
      }
      const activeMedia = visibleMedia.find((mediaItem) => mediaItem.classList.contains('is-active'))
        || visibleMedia[0]
        || mediaItems[0];
      this.updateGalleryMeta(activeMedia);
      this.updateNavigation(activeMedia);
    }

    getGalleryStateKey() {
      const mediaIds = this.getNavigableMedia().map((mediaItem) => mediaItem.dataset.mediaId);
      return `${this.dataset.ebSelectedVariantId || ''}|${mediaIds.join(',')}`;
    }

    rememberGalleryState() {
      this.galleryStateKey = this.getGalleryStateKey();
      this.userMediaId = null;
    }

    keepUserMedia() {
      if (!this.userMediaId || this.getGalleryStateKey() !== this.galleryStateKey) return false;

      const userMedia = this.getNavigableMedia().find((mediaItem) => mediaItem.dataset.mediaId === this.userMediaId);
      if (!userMedia) return false;

      // The caller may already have scrolled the viewer back, so return to the photo the shopper chose.
      const slider = userMedia.parentElement;
      const scrollBehavior = slider.style.scrollBehavior;
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = userMedia.offsetLeft;
      slider.style.scrollBehavior = scrollBehavior;

      this.keepUserThumbnailUntil = Date.now() + 1500;
      this.setActiveMediaState(userMedia);
      this.updateGalleryMeta(userMedia);
      this.updateNavigation(userMedia);
      this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${this.userMediaId}"]`));
      return true;
    }

    setActiveMediaState(activeMedia) {
      if (!activeMedia || !this.elements.viewer) return;

      this.elements.viewer.querySelectorAll('[data-eb-media-position][data-media-id]').forEach((mediaItem) => {
        mediaItem.classList.toggle('is-active', mediaItem === activeMedia);
      });
    }

    getNavigableMedia() {
      if (!this.elements.viewer) return [];

      const selectedBoundaryIndex = this.variantMediaBoundaries.findIndex(
        (boundary) => String(boundary.id) === String(this.dataset.ebSelectedVariantId)
      );
      const selectedBoundary = this.variantMediaBoundaries[selectedBoundaryIndex];
      const nextBoundary = this.variantMediaBoundaries[selectedBoundaryIndex + 1];
      const hasReliableVariantRange = this.variantMediaBoundaries.length > 1
        && this.variantMediaBoundaries[0].position === 1
        && selectedBoundaryIndex >= 0
        && selectedBoundary.position > 0;

      return Array.from(this.elements.viewer.querySelectorAll('[data-eb-media-position][data-media-id]'))
        .filter((mediaItem) => {
          if (mediaItem.hidden || mediaItem.classList.contains('hidden')) return false;
          if (mediaItem.dataset.ebActiveVariantMedia === 'false') return false;

          const position = Number(mediaItem.dataset.ebMediaPosition);
          if (hasReliableVariantRange
            && (position < selectedBoundary.position || (nextBoundary && position >= nextBoundary.position))) {
            return false;
          }

          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaItem.dataset.mediaId}"]`);
          return !thumbnail
            || (!thumbnail.hidden
              && !thumbnail.classList.contains('hidden')
              && thumbnail.dataset.ebActiveVariantThumbnail !== 'false');
        })
        .sort((firstMedia, secondMedia) => (
          Number(firstMedia.dataset.ebMediaPosition) - Number(secondMedia.dataset.ebMediaPosition)
        ));
    }

    navigateMedia(direction) {
      const mediaItems = this.getNavigableMedia();
      const activeMedia = mediaItems.find((mediaItem) => mediaItem.classList.contains('is-active')) || mediaItems[0];
      const activeIndex = mediaItems.indexOf(activeMedia);
      const targetMedia = mediaItems[activeIndex + direction];

      if (!targetMedia) {
        this.updateNavigation(activeMedia);
        return;
      }

      this.setActiveMedia(targetMedia.dataset.mediaId, false);
    }

    updateNavigation(activeMedia) {
      if (!this.elements.navigation) return;

      const mediaItems = this.getNavigableMedia();
      const currentMedia = mediaItems.includes(activeMedia)
        ? activeMedia
        : mediaItems.find((mediaItem) => mediaItem.classList.contains('is-active')) || mediaItems[0];
      const activeIndex = mediaItems.indexOf(currentMedia);
      const isSingleMedia = mediaItems.length <= 1;

      this.elements.navigation.hidden = isSingleMedia;
      this.elements.previous?.toggleAttribute('disabled', isSingleMedia || activeIndex <= 0);
      this.elements.next?.toggleAttribute('disabled', isSingleMedia || activeIndex >= mediaItems.length - 1);
    }

    syncGloboThumbnails(mediaItems) {
      const mediaVisibility = new Map(
        mediaItems.map((mediaItem) => [mediaItem.dataset.mediaId, mediaItem.dataset.ebActiveVariantMedia])
      );
      this.querySelectorAll('[data-target]').forEach((thumbnail) => {
        const isActiveVariant = mediaVisibility.get(thumbnail.dataset.target);
        if (!isActiveVariant) return;

        thumbnail.dataset.ebActiveVariantThumbnail = isActiveVariant;
        thumbnail.classList.toggle('hidden', isActiveVariant !== 'true');
      });
    }

    resetFilteredSlider(visibleCount) {
      const sliderComponent = this.elements.viewer;
      if (!sliderComponent?.slider) return;

      // v1.3.0: on page load the slider is already at its start, so only later variant changes rewind it.
      if (this.sliderRewindReady) sliderComponent.slider.scrollTo({ left: 0, behavior: 'auto' });
      this.sliderRewindReady = true;
      if (visibleCount > 1) return;

      runAfterLayout(() => {
        sliderComponent.sliderItemOffset = sliderComponent.slider.clientWidth || 1;
      });
      sliderComponent.slidesPerPage = 1;
      sliderComponent.totalPages = 1;
      sliderComponent.currentPage = 1;
      sliderComponent.prevButton?.setAttribute('disabled', 'disabled');
      sliderComponent.nextButton?.setAttribute('disabled', 'disabled');
    }

    getVariantMediaBoundaries() {
      const data = this.querySelector('[data-eb-variant-media-boundaries]');
      if (!data) return [];

      try {
        return JSON.parse(data.textContent)
          .filter((boundary) => Number(boundary.position) > 0)
          .sort((a, b) => Number(a.position) - Number(b.position));
      } catch (error) {
        console.error('Unable to parse product variant media boundaries.', error);
        return [];
      }
    }

    updateGalleryMeta(activeMedia) {
      const visibleMedia = Array.from(this.elements.viewer?.querySelectorAll('[data-eb-media-position]') || [])
        .filter((mediaItem) => !mediaItem.hidden);
      const activeIndex = Math.max(0, visibleMedia.indexOf(activeMedia));

      if (this.elements.current) this.elements.current.textContent = activeIndex + 1;
      if (this.elements.total) this.elements.total.textContent = Math.max(visibleMedia.length, 1);
      this.classList.toggle('eb-mobile-pdp-gallery--single', visibleMedia.length <= 1);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      // Globo repeats its thumbnail sync 300 ms after the skipped jump.
      if (this.keepUserThumbnailUntil > Date.now()) {
        thumbnail = this.elements.thumbnails.querySelector(`[data-target="${this.userMediaId}"]`) || thumbnail;
      }

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
  
      this.elements.thumbnails.querySelectorAll('.thumbnail').forEach((element) => {
        element.classList.remove('is-active');
      });
  
      thumbnail.querySelector('.thumbnail').classList.add('is-active');

      // v1.3.0: the visibility check reads geometry, so it waits for the browser's layout.
      const thumbnails = this.elements.thumbnails;
      runAfterLayout(() => {
        if (thumbnails.isSlideVisible(thumbnail, 10)) return;
        thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      const announce = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      if (image.complete && image.naturalWidth) {
        announce();
        return;
      }
      image.addEventListener('load', announce, { once: true });
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

class VerticalThumbnailSlider extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector("ul.slider");
    this.sliderItems = this.querySelectorAll("li.slider-slide");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.step = parseInt(this.prevButton?.dataset.step || "1", 10);
    this.sliderItemHeight = 0;

    if (!this.slider || !this.nextButton) return;

    this.init();

    this.prevButton.addEventListener("click", this.scrollUp.bind(this));
    this.nextButton.addEventListener("click", this.scrollDown.bind(this));
    this.slider.addEventListener("scroll", this.updateButtons.bind(this));

    const resizeObserver = new ResizeObserver(() => this.init());
    resizeObserver.observe(this.slider);
  }

  init() {
    this.sliderItems = this.querySelectorAll("li.slider-slide");
    if (this.sliderItems.length < 2) return;

    this.sliderItemHeight =
      this.sliderItems[1].offsetTop - this.sliderItems[0].offsetTop;

    this.updateButtons();
  }

  scrollUp(e) {
    e.preventDefault();
    this.slider.scrollBy({
      top: -this.step * this.sliderItemHeight,
      behavior: "smooth"
    });
  }

  scrollDown(e) {
    e.preventDefault();
    this.slider.scrollBy({
      top: this.step * this.sliderItemHeight,
      behavior: "smooth"
    });
  }

  updateButtons() {
    if (!this.slider) return;

    const scrollTop = this.slider.scrollTop;
    const maxScrollTop = this.slider.scrollHeight - this.slider.clientHeight;

    if (scrollTop <= 0) {
      this.prevButton.style.display = "none";  
    } else {
      this.prevButton.style.display = "flex"; 
    }

    if (scrollTop >= maxScrollTop - 1) {
      this.nextButton.style.display = "none";  
    } else {
      this.nextButton.style.display = "flex";  
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.scrollTop + this.slider.clientHeight - offset;
    return (
      element.offsetTop + element.clientHeight <= lastVisibleSlide &&
      element.offsetTop >= this.slider.scrollTop
    );
  }
}

customElements.define("vertical-thumbnail-slider", VerticalThumbnailSlider);
